import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { FloatingMenu } from './FloatingMenu'
import type { ActionMenuItem } from './ActionMenu'
import { OverflowTip } from './Hover/OverflowTip'
import { Pressable } from './Pressable'
import { getNlsString } from '../../nls/nlsRuntime'

export type ContextMenuProps = {
  open: boolean
  position: { x: number; y: number } | null
  items: ActionMenuItem[]
  ariaLabel: string
  onClose: () => void
  className?: string
  emptyLabel?: string
}

function enabledIndices(items: ActionMenuItem[]): number[] {
  return items
    .map((item, idx) => (item.disabled ? -1 : idx))
    .filter((idx) => idx !== -1)
}

/**
 * ContextMenu — cursor-point-anchored context menu primitive.
 * Positions an invisible fixed 0x0 anchor at the given viewport coordinates and renders
 * a FloatingMenu surface. Closes on Escape and outside pointerdown interaction.
 * Keyboard navigation follows ActionMenu's item model.
 */
export const ContextMenu = ({
  open,
  position,
  items,
  ariaLabel,
  onClose,
  className,
  emptyLabel = getNlsString('NLSCommonEmptyNoActions'),
}: ContextMenuProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null)

  // Reset focusedIdx on open/close transition during render
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    setFocusedIdx(null)
  }

  // Focus management
  useEffect(() => {
    if (open) {
      menuRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open || focusedIdx === null) return
    itemRefs.current[focusedIdx]?.focus()
  }, [open, focusedIdx])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const enabled = enabledIndices(items)
    if (enabled.length === 0) return

    const currentPos = focusedIdx === null ? -1 : enabled.indexOf(focusedIdx)

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        const next = currentPos < enabled.length - 1 ? currentPos + 1 : 0
        setFocusedIdx(enabled[next])
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const prev = currentPos > 0 ? currentPos - 1 : enabled.length - 1
        setFocusedIdx(enabled[prev])
        break
      }
      case 'Home': {
        event.preventDefault()
        setFocusedIdx(enabled[0])
        break
      }
      case 'End': {
        event.preventDefault()
        setFocusedIdx(enabled[enabled.length - 1])
        break
      }
      case 'Enter':
      case ' ': {
        if (focusedIdx !== null && items[focusedIdx] && !items[focusedIdx].disabled) {
          event.preventDefault()
          items[focusedIdx].onSelect()
          onClose()
        }
        break
      }
      default:
        break
    }
  }

  if (!open || !position) return null

  return (
    <>
      <div
        ref={anchorRef}
        data-context-menu-anchor="true"
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: 0,
          height: 0,
          pointerEvents: 'none',
          opacity: 0,
        }}
      />
      <FloatingMenu
        anchorRef={anchorRef as RefObject<HTMLElement | null>}
        isOpen={open}
        onClose={onClose}
        placement="bottom-start"
        offset={0}
        className={`dsDrop is-open${className ? ` ${className}` : ''}`}
        role="menu"
        ariaLabel={ariaLabel}
      >
        <div
          ref={menuRef}
          className="dsDrop__items"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          {items.length === 0 ? (
            <span className="dsDrop__empty">{emptyLabel}</span>
          ) : (
            items.map((item, idx) => (
              <Fragment key={item.key}>
                {item.dividerBefore ? <div className="dsDrop__divider" role="separator" /> : null}
                {item.groupHeading ? (
                  <div className="dsDrop__groupHeader" role="presentation">
                    {item.groupHeading}
                  </div>
                ) : null}
                <Pressable
                  ref={(node) => {
                    itemRefs.current[idx] = node
                  }}
                  role="menuitem"
                  tabIndex={focusedIdx === idx ? 0 : -1}
                  disabled={item.disabled}
                  title={item.title}
                  data-tone={item.tone === 'warn' ? 'warn' : undefined}
                  className={`dsDrop__item${item.isActive ? ' is-active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (item.disabled) return
                    item.onSelect()
                    onClose()
                  }}
                >
                  {item.icon && (
                    <span className="dsDrop__itemIcon" aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  {typeof item.label === 'string' || typeof item.label === 'number' ? (
                    <OverflowTip label={String(item.label)}>
                      <span className="dsDrop__itemLabel">{item.label}</span>
                    </OverflowTip>
                  ) : (
                    <span className="dsDrop__itemLabel">{item.label}</span>
                  )}
                  {item.meta && (
                    <span className="dsDrop__itemMeta">{item.meta}</span>
                  )}
                </Pressable>
              </Fragment>
            ))
          )}
        </div>
      </FloatingMenu>
    </>
  )
}
